// ABOUTME: The hooks the applications screens render from: which arm of the tracker experiment the talent is in, the
// ABOUTME: list newest first, one application with its timeline, and the withdrawal, each turning the mapped error
// ABOUTME: codes into the state the screen branches on.
import { useMemo } from 'react';
import { FeatureFlag, useFeatureVariant } from 'modules/unleash';
import { ApiError } from 'utils/api';
import {
  APPLICATION_NOT_FOUND_ERROR_CODE,
  APPLICATIONS_UNAVAILABLE_ERROR_CODE,
  ATS_CONTROL_VARIANT,
  ATS_TRACKER_VARIANT,
  UNNAMED_ERROR_CODE
} from './constants';
import {
  useApplicationQuery,
  useApplicationsQuery,
  useSetApplicationHiddenMutation,
  useWithdrawApplicationMutation
} from './query';
import { TalentApplication } from './types';
import { movedTodayFirst } from './utils';

export interface TrackerArm {
  /** The arm as it is reported, never undefined: a talent is in the tracker arm or they are in control. */
  variant: string;
  isTracker: boolean;
  /** The proxy has not answered for this talent yet, so there is no arm to act on. */
  isLoading: boolean;
}

/**
 * Which half of the tracker experiment this talent is in, in the one place that decides it.
 *
 * Only the named tracker arm sees the tracker. Everything else is control: the flag off, an arm by another name, a
 * proxy that could not be reached. Control is the safe side, because it is what every talent has today.
 */
export const useTrackerArm = (): TrackerArm => {
  const { variant, isLoading } = useFeatureVariant(FeatureFlag.PRO_ATS);
  const isTracker = variant === ATS_TRACKER_VARIANT;

  return {
    variant: isTracker ? ATS_TRACKER_VARIANT : ATS_CONTROL_VARIANT,
    isTracker,
    isLoading
  };
};

export interface ApplicationsState {
  /** Newest first by `appliedAt`; undefined until loaded, or when the list could not be loaded. */
  applications: TalentApplication[] | undefined;
  isLoading: boolean;
  /** A genuine failure. Never true for the off state below. */
  isError: boolean;
  /** PRO_ATS is off for this talent (the backend answered 404): the friendly off state, not an error. */
  isUnavailable: boolean;
  error: unknown;
}

const codeOf = (error: unknown): string | null =>
  error instanceof ApiError ? error.code : null;

const isCode = (error: unknown, code: string): boolean =>
  codeOf(error) === code;

/**
 * Newest first by `appliedAt`. The backend already orders this way; sorting here keeps the promise if that ever
 * changes. An application with no history yet has no `appliedAt` and goes last: it cannot be newer than a dated one.
 */
const newestFirst = (applications: TalentApplication[]): TalentApplication[] =>
  [...applications].sort((a, b) =>
    (b.appliedAt ?? '').localeCompare(a.appliedAt ?? '')
  );

export const useApplications = (): ApplicationsState => {
  const { data, isIdle, isLoading, isError, error } = useApplicationsQuery();

  // Newest first, and whatever moved today in front of that: the change is what a talent opens the list for.
  const applications = useMemo(
    () => (data ? movedTodayFirst(newestFirst(data), new Date()) : undefined),
    [data]
  );
  const isUnavailable =
    isError && isCode(error, APPLICATIONS_UNAVAILABLE_ERROR_CODE);

  return {
    applications,
    // Idle means the query is waiting for the Auth0 token: still loading as far as the screen is concerned.
    isLoading: isLoading || isIdle,
    isError: isError && !isUnavailable,
    isUnavailable,
    error
  };
};

export interface ApplicationState {
  application: TalentApplication | undefined;
  isLoading: boolean;
  isError: boolean;
  /** PRO_ATS is off for this talent. */
  isUnavailable: boolean;
  /** The refId is not one of this talent's applications, or it no longer exists. */
  isNotFound: boolean;
  error: unknown;
}

/**
 * One application, with the two 404s kept apart: "this is not yours" is a dead end for this refId alone, "the feature
 * is off" is the same off state the list shows. Everything else is an error.
 */
export const useApplication = (refId: string | undefined): ApplicationState => {
  const { data, isIdle, isLoading, isError, error } =
    useApplicationQuery(refId);

  const isUnavailable =
    isError && isCode(error, APPLICATIONS_UNAVAILABLE_ERROR_CODE);
  const isNotFound = isError && isCode(error, APPLICATION_NOT_FOUND_ERROR_CODE);

  return {
    application: data,
    isLoading: isLoading || isIdle,
    isError: isError && !isUnavailable && !isNotFound,
    isUnavailable,
    isNotFound,
    error
  };
};

export interface HideApplicationState {
  /** Hide or show one row; rejects with the ApiError, so the caller can say it did not work. */
  setHidden: (refId: string, hidden: boolean) => Promise<TalentApplication>;
  isSaving: boolean;
}

/** PJL: hiding an application from the talent's own list, and showing it again (the toast's undo, the filter's row). */
export const useHideApplication = (): HideApplicationState => {
  const { mutateAsync, isLoading } = useSetApplicationHiddenMutation();
  return {
    setHidden: (refId, hidden) => mutateAsync({ refId, hidden }),
    isSaving: isLoading
  };
};

export interface WithdrawState {
  withdraw: (reasonText: string, options?: { onSuccess?: () => void }) => void;
  isWithdrawing: boolean;
  /**
   * The code of a refused withdrawal, which the screen turns into copy; null while nothing failed. A failure pro-core
   * did not name arrives as UNNAMED_ERROR_CODE rather than as null, so "it failed" is never mistaken for "it did not".
   */
  errorCode: string | null;
  reset: () => void;
}

export const useWithdrawApplication = (
  refId: string | undefined
): WithdrawState => {
  const { mutate, isLoading, error, reset } =
    useWithdrawApplicationMutation(refId);

  return {
    withdraw: mutate,
    isWithdrawing: isLoading,
    errorCode: error ? codeOf(error) ?? UNNAMED_ERROR_CODE : null,
    reset
  };
};
